import React, { useState, useEffect } from 'react';
import { goBackOrHome } from '@/lib/navHelpers';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Play, Clock, Headphones, Video, Search, Filter, ArrowLeft, Home } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import MeditationPlayer from '@/components/MeditationPlayer';

interface Meditation {
  id: string;
  title: string;
  description: string;
  category: string;
  audio_url: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  duration_minutes: number | null;
  difficulty_level: string;
  meditation_type: string;
  tags: string[];
  play_count: number;
  created_at: string;
}

const MeditationLibrary = () => {
  const navigate = useNavigate();
  const { trackPageView, trackContentInteraction, trackFeatureUsage } = useAnalytics();
  const [meditations, setMeditations] = useState<Meditation[]>([]);
  const [filteredMeditations, setFilteredMeditations] = useState<Meditation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMeditation, setSelectedMeditation] = useState<Meditation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');

  // Get URL params for direct meditation access and return path
  const urlParams = new URLSearchParams(window.location.search);
  const meditationId = urlParams.get('meditationId');
  const returnPath = urlParams.get('returnPath');

  useEffect(() => {
    loadPublishedMeditations();
    trackPageView('meditation_library');
  }, []);

  useEffect(() => {
    // Auto-play meditation if meditationId is in URL
    if (meditationId && meditations.length > 0) {
      const meditation = meditations.find(m => m.id === meditationId);
      if (meditation) {
        handlePlayMeditation(meditation);
      }
    }
  }, [meditationId, meditations]);

  useEffect(() => {
    filterMeditations();
  }, [meditations, searchTerm, selectedCategory, selectedType, selectedDifficulty]);

  const loadPublishedMeditations = async () => {
    try {
      const { data, error } = await supabase
        .from('meditations')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMeditations(data || []);
    } catch (error) {
      console.error('Error loading meditations:', error);
      toast.error('Failed to load meditations');
    } finally {
      setLoading(false);
    }
  };

  const filterMeditations = () => {
    let filtered = meditations;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(meditation =>
        meditation.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        meditation.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        meditation.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(meditation => meditation.category === selectedCategory);
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(meditation => meditation.meditation_type === selectedType);
    }

    // Difficulty filter
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(meditation => meditation.difficulty_level === selectedDifficulty);
    }

    setFilteredMeditations(filtered);
  };

  const handlePlayMeditation = async (meditation: Meditation) => {
    trackContentInteraction('meditation', meditation.id, 'play_started', {
      meditation_title: meditation.title,
      meditation_category: meditation.category,
      meditation_type: meditation.meditation_type,
      meditation_duration: meditation.duration_minutes
    });
    setSelectedMeditation(meditation);
    
    // Increment play count
    try {
      await supabase
        .from('meditations')
        .update({ play_count: meditation.play_count + 1 })
        .eq('id', meditation.id);
      
      // Update local state
      setMeditations(prev => prev.map(m => 
        m.id === meditation.id 
          ? { ...m, play_count: m.play_count + 1 }
          : m
      ));
    } catch (error) {
      console.error('Error updating play count:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedType('all');
    setSelectedDifficulty('all');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Navigation */}
          <div className="flex items-center gap-2 mb-6">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => goBackOrHome(navigate)}
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </div>

          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 sm:mb-4">Guided Meditations</h1>
            <p className="text-sm sm:text-base lg:text-lg text-muted-foreground px-4">Find peace and re-programme your life with our collection of meditation and hypnosis sessions.</p>
          </div>

          {/* Search and Filters */}
          <Card className="p-6 mb-8">
            <div className="grid md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search meditations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="sleep">Sleep</SelectItem>
                  <SelectItem value="anxiety">Anxiety Relief</SelectItem>
                  <SelectItem value="focus">Focus & Concentration</SelectItem>
                  <SelectItem value="healing">Healing</SelectItem>
                  <SelectItem value="spiritual">Spiritual</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="guided">Guided</SelectItem>
                  <SelectItem value="hypnosis">Hypnosis</SelectItem>
                  <SelectItem value="visualization">Visualization</SelectItem>
                  <SelectItem value="breathwork">Breathwork</SelectItem>
                  <SelectItem value="soundscape">Soundscape</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                <SelectTrigger>
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(searchTerm || selectedCategory !== 'all' || selectedType !== 'all' || selectedDifficulty !== 'all') && (
              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredMeditations.length} of {meditations.length} meditations
                </p>
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <Filter className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            )}
          </Card>

          {/* Meditations Grid */}
          {filteredMeditations.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="max-w-md mx-auto">
                {meditations.length === 0 ? (
                  <>
                    <Headphones className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No meditations available</h3>
                    <p className="text-muted-foreground">Check back soon for new meditations and guided sessions.</p>
                  </>
                ) : (
                  <>
                    <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No results found</h3>
                    <p className="text-muted-foreground mb-4">Try adjusting your search or filters to find what you're looking for.</p>
                    <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
                  </>
                )}
              </div>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMeditations.map((meditation) => (
                <Card key={meditation.id} className="group hover:shadow-gentle transition-all duration-300 hover:scale-105 overflow-hidden">
                  {/* Thumbnail or Placeholder */}
                  <div className="aspect-video bg-gradient-therapeutic relative overflow-hidden">
                    {meditation.thumbnail_url ? (
                      <img 
                        src={meditation.thumbnail_url} 
                        alt={meditation.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {meditation.video_url ? (
                          <Video className="h-12 w-12 text-primary/60" />
                        ) : (
                          <Headphones className="h-12 w-12 text-primary/60" />
                        )}
                      </div>
                    )}
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-background/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="lg"
                        className="rounded-full w-16 h-16"
                        onClick={() => handlePlayMeditation(meditation)}
                      >
                        <Play className="h-8 w-8" />
                      </Button>
                    </div>
                    
                    {/* Duration Badge */}
                    {meditation.duration_minutes && (
                      <div className="absolute top-2 right-2 bg-background/70 text-foreground px-2 py-1 rounded text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {meditation.duration_minutes}m
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
                        {meditation.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {meditation.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <Badge variant="secondary">{meditation.category}</Badge>
                      <Badge variant="outline">{meditation.meditation_type}</Badge>
                      <Badge variant="outline">{meditation.difficulty_level}</Badge>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{meditation.play_count} plays</span>
                        {meditation.audio_url && meditation.video_url && (
                          <span className="flex items-center gap-1">
                            <Video className="h-3 w-3" />
                            <Headphones className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                      
                      <Button
                        size="sm"
                        onClick={() => handlePlayMeditation(meditation)}
                        className="flex items-center gap-2"
                      >
                        <Play className="h-3 w-3" />
                        Play
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Meditation Player Modal */}
      {selectedMeditation && (
        <MeditationPlayer
          title={selectedMeditation.title}
          audioUrl={selectedMeditation.audio_url || undefined}
          videoUrl={selectedMeditation.video_url || undefined}
          onClose={() => {
            setSelectedMeditation(null);
            // Navigate back to return path if provided
            if (returnPath) {
              navigate(returnPath);
            }
          }}
        />
      )}
    </div>
  );
};

export default MeditationLibrary;